import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePermissions } from '../context/PermissionsContext';
import { apiClient } from '../api';
import { formatDateTime } from '../utils/stringUtils';
import { DeleteButton } from './shared/DeleteButton';

interface MpxTenant {
  id: string;
  config_id: string;
  name: string;
  description: string;
  api_endpoint: string;
  our_did: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

const MpxTenantsPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { id: idParam } = useParams<{ id?: string }>();
  const [tenants, setTenants] = useState<MpxTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expandedTenantIds, setExpandedTenantIds] = useState<Set<string>>(new Set());
  const tenantRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [pingStatus, setPingStatus] = useState<{ [key: string]: 'pinging' | 'success' | 'failed' }>({});

  useEffect(() => {
    fetchTenants();
  }, []);

  // Handle deep linking to a specific tenant
  useEffect(() => {
    if (idParam && tenants.length > 0) {
      const decodedId = decodeURIComponent(idParam);
      const tenant = tenants.find(t => t.id === decodedId);
      if (tenant) {
        setExpandedTenantIds(new Set([decodedId]));
        // Scroll to the tenant after a short delay
        setTimeout(() => {
          const element = tenantRefs.current[decodedId];
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    }
  }, [idParam, tenants]);

  // Auto-redirect to wizard if no tenants exist
  useEffect(() => {
    if (!loading && tenants.length === 0 && !error && hasPermission('mpx_tenants.edit')) {
      navigate('/mpx-tenants/wizard');
    }
  }, [loading, tenants.length, error, hasPermission, navigate]);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/mpx-tenants');
      setTenants(response.data);
    } catch (error: any) {
      setError(error.message || 'Failed to load MPX tenants');
    } finally {
      setLoading(false);
    }
  };

  const toggleTenantDetails = (tenantId: string) => {
    // Navigate directly to edit page instead of toggling expansion
    navigate(`/mpx-tenants/edit/${encodeURIComponent(tenantId)}`);
  };

  const handleDelete = async (id: string) => {
    try {
      await apiClient.delete(`/mpx-tenants/${id}`);
      setSuccess(`MPX Tenant deleted successfully`);
      fetchTenants();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.message || 'Failed to delete MPX tenant');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handlePing = async (e: React.MouseEvent, tenant: MpxTenant) => {
    e.stopPropagation();
    
    setPingStatus(prev => ({ ...prev, [tenant.id]: 'pinging' }));
    
    try {
      const response = await apiClient.post('/mpx-tenants/ping', {
        tenant_id: tenant.id,
      });
      
      if (response.data.success) {
        setPingStatus(prev => ({ ...prev, [tenant.id]: 'success' }));
      } else {
        setPingStatus(prev => ({ ...prev, [tenant.id]: 'failed' }));
      }
    } catch (error: any) {
      setPingStatus(prev => ({ ...prev, [tenant.id]: 'failed' }));
    }
    
    // Reset status after 3 seconds
    setTimeout(() => {
      setPingStatus(prev => {
        const newStatus = { ...prev };
        delete newStatus[tenant.id];
        return newStatus;
      });
    }, 3000);
  };

  const filteredTenants = tenants.filter(tenant => 
    searchTerm === '' || 
    tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tenant.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tenant.api_endpoint.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h3 mb-0 text-gray-800">
            <i className="fas fa-cloud"></i> MPX Tenants
          </h1>
          <p className="text-muted mt-2">
            Manage Meeting Place (MPX) Control Plane API connections
          </p>
        </div>
        {hasPermission('mpx_tenants.edit') && tenants.length > 0 && (
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/mpx-tenants/wizard')}
          >
            <i className="fas fa-plus me-2"></i> Add MPX Tenant
          </button>
        )}
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
          <button type="button" className="close" onClick={() => setError(null)}>
            <span>&times;</span>
          </button>
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <i className="fas fa-check-circle me-2"></i>
          {success}
          <button type="button" className="close" onClick={() => setSuccess(null)}>
            <span>&times;</span>
          </button>
        </div>
      )}

      {tenants.length > 0 && (
        <div className="mb-4">
          <input
            type="text"
            className="form-control"
            placeholder="Search tenants by name, description, or API endpoint..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      )}

      <div className="card shadow">
        <div className="card-body">
          {filteredTenants.length === 0 ? (
            <div className="text-center text-muted py-5">
              <i className="fas fa-cloud fa-4x mb-3" style={{ opacity: 0.3 }}></i>
              <h5>{searchTerm ? 'No MPX Tenants match your search' : 'No MPX Tenants configured'}</h5>
              <p className="mt-2">
                {searchTerm 
                  ? 'Try adjusting your search criteria' 
                  : 'Click the button above to add your first MPX Tenant connection'}
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '5%' }}></th>
                    <th style={{ width: '20%' }}>Name</th>
                    <th style={{ width: '25%' }}>Description</th>
                    <th style={{ width: '25%' }}>API Endpoint</th>
                    <th style={{ width: '10%' }}>Status</th>
                    <th style={{ width: '15%', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTenants.map((tenant) => {
                    const isExpanded = expandedTenantIds.has(tenant.id);
                    const currentPingStatus = pingStatus[tenant.id];

                    return (
                      <React.Fragment key={tenant.id}>
                        <tr 
                          ref={(el) => { tenantRefs.current[tenant.id] = el; }}
                          onClick={() => toggleTenantDetails(tenant.id)}
                          style={{ cursor: 'pointer' }}
                          className={isExpanded ? 'table-active' : ''}
                        >
                          <td>
                            <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                          </td>
                          <td>
                            <strong>{tenant.name}</strong>
                          </td>
                          <td>{tenant.description}</td>
                          <td>
                            <code style={{ fontSize: '0.85rem' }}>{tenant.api_endpoint}</code>
                          </td>
                          <td>
                            <span className={`badge badge-${tenant.enabled ? 'success' : 'secondary'}`}>
                              {tenant.enabled ? 'ENABLED' : 'DISABLED'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <button
                              className="btn btn-sm btn-info me-2"
                              onClick={(e) => handlePing(e, tenant)}
                              disabled={currentPingStatus === 'pinging'}
                              title=" Test connection"
                              style={{ marginRight: '5px' }}
                            >
                              {currentPingStatus === 'pinging' ? (
                                <i className="fas fa-spinner fa-spin"></i>
                              ) : currentPingStatus === 'success' ? (
                                <i className="fas fa-check text-success"></i>
                              ) : currentPingStatus === 'failed' ? (
                                <i className="fas fa-times text-danger"></i>
                              ) : (
                                <i className="fas fa-heartbeat"></i>
                              )}
                            </button>
                            {hasPermission('mpx_tenants.edit') && (
                              <>
                                <button
                                  className="btn btn-sm btn-primary me-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/mpx-tenants/edit/${tenant.id}`);
                                  }}
                                  title="Edit"
                                  style={{ marginRight: '5px' }}
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <DeleteButton
                                  onDelete={() => handleDelete(tenant.id)}
                                  title="Delete MPX Tenant"
                                />
                              </>
                            )}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={6} className="bg-light">
                              <div className="p-4">
                                <h6 className="text-primary mb-3">
                                  <i className="fas fa-info-circle me-2"></i>
                                  Tenant Details
                                </h6>
                                <div className="row">
                                  <div className="col-md-6">
                                    <div className="mb-3">
                                      <strong>Configuration ID:</strong>
                                      <div className="mt-1">
                                        <code>{tenant.config_id}</code>
                                      </div>
                                    </div>
                                    <div className="mb-3">
                                      <strong>API Endpoint:</strong>
                                      <div className="mt-1">
                                        <code>{tenant.api_endpoint}</code>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-md-6">
                                    <div className="mb-3">
                                      <strong>Created:</strong>
                                      <div className="mt-1 text-muted">
                                        {formatDateTime(tenant.created_at)}
                                      </div>
                                    </div>
                                    <div className="mb-3">
                                      <strong>Last Updated:</strong>
                                      <div className="mt-1 text-muted">
                                        {formatDateTime(tenant.updated_at)}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="mt-3">
                                  <strong>Description:</strong>
                                  <p className="mt-1 text-muted">{tenant.description || 'No description provided'}</p>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MpxTenantsPage;
